import * as React from 'react';
import { ApiHatasi, api } from '@/lib/api';
import { anMs, paraFormatla } from '@/lib/utils';
import { Kontenjan } from './Kontenjan';
import { Takvim } from './Takvim';

/**
 * Kanal yonetimi.
 *
 * Tasarim kaynagi: Claude Design — "otelcms Kanal Yonetimi.dc.html".
 *
 * Tasarim Booking.com, Expedia, Airbnb, Trivago gibi kanallari "bagli ve
 * senkron" gosteriyordu; bunlarin hicbirine dogrudan baglanti YOK ve olmasi
 * imzali ticari sozlesme + saglayici sertifikasyonu gerektirir. Bagli
 * olmayan bir kanali bagliymis gibi gostermek otelcinin odalarini satista
 * saniip fazla satmasina yol acar. Bu yuzden ekran yalnizca gercekten
 * calisan kanallari listeler, digerleri icin ne gerektigini acikca yazar.
 */

interface Anahtar {
  id: string;
  ad: string;
  prefix: string;
  aktif: boolean;
  sonKullanim: string | number | null;
  dakikalikLimit: number;
}

interface Kanal {
  id: string;
  tip: 'DAHILI' | 'ACENTA' | 'OTA';
  ad: string;
  aciklama: string;
  kod?: string;
  komisyonYuzde: number;
  aktif: boolean;
  anahtar: Anahtar | null;
  kontenjan?: { toplam: number; kullanilan: number } | null;
  buAy: { rezervasyon: number; gelirKurus: number; komisyonKurus: number };
}

interface FiyatSatiri {
  kanalId: string;
  ozelPlanVarMi: boolean;
  satisKurus: number;
  netKurus: number;
}

interface Veri {
  donemBasi: string;
  ornekTarih: string;
  ozet: {
    toplamGelirKurus: number;
    komisyonKurus: number;
    dogrudanGelirKurus: number;
    dogrudanPayYuzde: number | null;
    rezervasyon: number;
  };
  kanallar: Kanal[];
  fiyatDagitimi: { roomTypeId: string; ad: string; odaSayisi: number; bazKurus: number; satirlar: FiyatSatiri[] }[];
  gunluk: {
    id: string;
    kod: string;
    kaynak: string;
    agencyId: string | null;
    durum: string;
    girisTarihi: string;
    cikisTarihi: string;
    toplamKurus: number;
    createdAt: string | number;
  }[];
}

type Sekme = 'kanallar' | 'kontenjan' | 'takvim' | 'fiyat' | 'gunluk';

const KAYNAK_ADI: Record<string, string> = {
  WEB: 'Web sitesi',
  ACENTA: 'Acenta',
  KANAL: 'Kanal',
  TELEFON: 'Telefon',
  WALK_IN: 'Kapıdan',
  DIREKT: 'Doğrudan',
};

const tl = (kurus: number) => paraFormatla(kurus, 'TRY');

function anEtiket(deger: string | number): string {
  const ms = anMs(deger);
  if (ms === null) return '—';
  return new Date(ms).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function harf(ad: string): string {
  return ad.trim().charAt(0).toLocaleUpperCase('tr-TR');
}

const RENKLER = ['#1f4fd8', '#0f7a3d', '#b9840f', '#7b2cbf', '#c74d3e', '#0891b2'];
const renkSec = (id: string) => RENKLER[[...id].reduce((t, c) => t + c.charCodeAt(0), 0) % RENKLER.length]!;

export function Kanallar({ yetkiler }: { yetkiler: string[] }) {
  const yazabilir = yetkiler.includes('agency:write');
  const [sekme, setSekme] = React.useState<Sekme>('kanallar');
  const [veri, setVeri] = React.useState<Veri | null>(null);
  const [yukleniyor, setYukleniyor] = React.useState(true);
  const [hata, setHata] = React.useState<string | null>(null);
  const [yeniAnahtar, setYeniAnahtar] = React.useState<{ ad: string; anahtar: string } | null>(null);
  const [islenen, setIslenen] = React.useState<string | null>(null);

  const yukle = React.useCallback(async () => {
    try {
      setVeri(await api<Veri>('/api/kanallar'));
      setHata(null);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Kanallar yüklenemedi.');
    } finally {
      setYukleniyor(false);
    }
  }, []);

  React.useEffect(() => {
    void yukle();
  }, [yukle]);

  async function anahtarUret(k: Kanal) {
    setIslenen(k.id);
    setHata(null);
    try {
      const cevap = await api<{ anahtar: string }>(`/api/kanallar/acentalar/${k.id}/anahtar`, {
        method: 'POST',
        body: JSON.stringify({ ad: `${k.ad} entegrasyonu` }),
      });
      setYeniAnahtar({ ad: k.ad, anahtar: cevap.anahtar });
      await yukle();
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Anahtar üretilemedi.');
    } finally {
      setIslenen(null);
    }
  }

  async function anahtarIptal(k: Kanal) {
    if (!k.anahtar) return;
    setIslenen(k.id);
    try {
      await api(`/api/kanallar/anahtarlar/${k.anahtar.id}/iptal`, { method: 'POST' });
      await yukle();
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Anahtar iptal edilemedi.');
    } finally {
      setIslenen(null);
    }
  }

  const acentalar = veri?.kanallar.filter((k) => k.tip !== 'DAHILI') ?? [];
  const bagliSayisi = veri?.kanallar.filter((k) => k.tip === 'DAHILI' || k.anahtar?.aktif).length ?? 0;

  return (
    <main className="flex flex-1 flex-col gap-[22px] bg-zemin p-5 sm:p-7">
      <div className="flex flex-wrap items-center gap-3.5 [animation:rise_.5s_cubic-bezier(.2,.7,.2,1)_both]">
        <h1 className="text-3xl font-bold tracking-[-.03em]">Kanal yönetimi</h1>
        <span className="flex items-center gap-2 rounded-full bg-yesil-zemin px-3 py-1.5 text-[13px] font-semibold text-yesil">
          <span className="relative size-2">
            <span className="absolute inset-0 rounded-full bg-yesil" />
            <span className="absolute inset-0 rounded-full border-[1.5px] border-yesil [animation:ring_1.8s_ease-out_infinite]" />
          </span>
          {bagliSayisi} kanal açık
        </span>
        <nav className="flex w-full gap-1 rounded-full border border-cizgi bg-white p-1 sm:ml-auto sm:w-auto overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {(
            [
              ['kanallar', 'Kanallar'],
              ['kontenjan', 'Kontenjan'],
              ['takvim', 'Takvim paylaşımı'],
              ['fiyat', 'Fiyat dağıtımı'],
              ['gunluk', 'Günlük'],
            ] as [Sekme, string][]
          ).map(([k, etiket]) => (
            <button
              key={k}
              onClick={() => setSekme(k)}
              className={
                'whitespace-nowrap rounded-full px-4 py-[7px] text-[13px] font-semibold transition-colors ' +
                (sekme === k ? 'bg-lacivert text-white' : 'text-gri hover:text-lacivert')
              }
            >
              {etiket}
            </button>
          ))}
        </nav>
      </div>

      {hata && (
        <p role="alert" className="rounded-2xl border border-kirmizi/30 bg-[#fdeceb] px-4 py-3 text-sm font-medium text-kirmizi">
          {hata}
        </p>
      )}

      {yukleniyor || !veri ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-2xl bg-white" />
          ))}
        </div>
      ) : (
        <>
          <Ozet veri={veri} />

          {sekme === 'kanallar' && (
            <KanalKartlari
              veri={veri}
              yazabilir={yazabilir}
              islenen={islenen}
              onUret={anahtarUret}
              onIptal={anahtarIptal}
            />
          )}
          {sekme === 'kontenjan' && <Kontenjan yetkiler={yetkiler} />}
          {sekme === 'takvim' && <Takvim yetkiler={yetkiler} />}
          {sekme === 'fiyat' && <FiyatDagitimi veri={veri} acentalar={acentalar} />}
          {sekme === 'gunluk' && <Gunluk veri={veri} />}
        </>
      )}

      {yeniAnahtar && <AnahtarPenceresi bilgi={yeniAnahtar} onKapat={() => setYeniAnahtar(null)} />}
    </main>
  );
}

/* ------------------------------------------------------------------ ozet */

function Ozet({ veri }: { veri: Veri }) {
  const ay = new Date(`${veri.donemBasi}T00:00:00`).toLocaleDateString('tr-TR', { month: 'long' });
  const kartlar: [string, string, string, string][] = [
    ['Kanal geliri', tl(veri.ozet.toplamGelirKurus), `${ay} · ${veri.ozet.rezervasyon} rezervasyon`, '#0b1530'],
    ['Komisyon', tl(veri.ozet.komisyonKurus), 'acenta sözleşmelerinden', '#e5484d'],
    [
      'Doğrudan pay',
      veri.ozet.dogrudanPayYuzde === null ? '—' : `%${veri.ozet.dogrudanPayYuzde}`,
      'web + telefon + kapıdan, komisyonsuz',
      '#12a150',
    ],
    ['Net kalan', tl(veri.ozet.toplamGelirKurus - veri.ozet.komisyonKurus), 'komisyon düşülmüş', '#1f4fd8'],
  ];
  return (
    <div className="grid gap-3 [animation:rise_.6s_.05s_cubic-bezier(.2,.7,.2,1)_both] sm:grid-cols-2 xl:grid-cols-4">
      {kartlar.map(([etiket, deger, alt, renk]) => (
        <div key={etiket} className="flex flex-col gap-1.5 rounded-2xl border border-cizgi bg-white p-[18px]">
          <span className="text-xs font-semibold uppercase tracking-[.08em] text-gri">{etiket}</span>
          <span className="text-[26px] font-bold leading-none tracking-[-.03em]" style={{ color: renk }}>
            {deger}
          </span>
          <span className="text-[13px] text-gri">{alt}</span>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------- kanallar */

function KanalKartlari({
  veri,
  yazabilir,
  islenen,
  onUret,
  onIptal,
}: {
  veri: Veri;
  yazabilir: boolean;
  islenen: string | null;
  onUret: (k: Kanal) => void;
  onIptal: (k: Kanal) => void;
}) {
  return (
    <>
      <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-3">
        {veri.kanallar.map((k, i) => {
          const bagli = k.tip === 'DAHILI' || !!k.anahtar?.aktif;
          return (
            <div
              key={k.id}
              className="flex flex-col gap-3.5 rounded-[20px] border border-cizgi bg-white p-5 transition hover:-translate-y-0.5 hover:border-marka-600 hover:shadow-[0_22px_40px_-28px_rgba(11,21,48,.35)] [animation:popin_.45s_cubic-bezier(.2,.8,.2,1)_both]"
              style={{ animationDelay: `${i * 40}ms` }}
            >
              <div className="flex items-center gap-3">
                <span
                  className="grid size-11 flex-none place-items-center rounded-xl text-base font-bold tracking-[-.02em] text-white"
                  style={{ background: k.tip === 'DAHILI' ? '#0b1530' : renkSec(k.id) }}
                >
                  {harf(k.ad)}
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-base font-bold tracking-[-.01em] text-lacivert">{k.ad}</span>
                  <span className="truncate text-xs text-gri">{k.aciklama}</span>
                </div>
                <span
                  className="flex-none rounded-full px-2.5 py-1 text-[11px] font-bold"
                  style={
                    bagli
                      ? { background: '#e6f7ec', color: '#0f7a3d' }
                      : { background: '#f4f6fb', color: '#5b6480' }
                  }
                >
                  {k.tip === 'DAHILI' ? 'kendi kanalınız' : bagli ? 'bağlı' : 'bağlı değil'}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-2 border-t border-satir pt-3 text-xs text-gri">
                <div className="flex flex-col gap-0.5">
                  <span>Bu ay</span>
                  <strong className="text-[15px] text-lacivert">{k.buAy.rezervasyon}</strong>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span>Komisyon</span>
                  <strong className="text-[15px] text-lacivert">
                    {k.komisyonYuzde > 0 ? `%${k.komisyonYuzde}` : 'yok'}
                  </strong>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span>Gelir</span>
                  <strong className="text-[15px] text-lacivert">
                    {k.buAy.gelirKurus > 0 ? tl(k.buAy.gelirKurus) : '—'}
                  </strong>
                </div>
              </div>

              {k.kontenjan && (
                <p className="rounded-xl bg-zemin px-3 py-2 text-[12.5px] text-gri">
                  Kontenjan: <strong className="text-lacivert">{k.kontenjan.kullanilan}</strong> /{' '}
                  {k.kontenjan.toplam} oda kullanıldı
                </p>
              )}

              {k.tip === 'DAHILI' ? (
                <p className="text-[12.5px] leading-relaxed text-gri">
                  Bu kanal her zaman açıktır; ayrı bir bağlantı gerekmez.
                </p>
              ) : k.anahtar?.aktif ? (
                <div className="flex flex-col gap-2">
                  <p className="rounded-xl bg-mavi-zemin px-3 py-2 font-mono text-[12px] text-lacivert">
                    {k.anahtar.prefix}…
                  </p>
                  <p className="text-[12px] text-gri">
                    Son kullanım: {k.anahtar.sonKullanim ? anEtiket(k.anahtar.sonKullanim) : 'henüz kullanılmadı'} ·{' '}
                    dakikada {k.anahtar.dakikalikLimit} istek
                  </p>
                  {yazabilir && (
                    <button
                      onClick={() => onIptal(k)}
                      disabled={islenen === k.id}
                      className="rounded-full border-[1.5px] border-cizgi bg-white py-2 text-[13px] font-semibold text-kirmizi transition hover:border-kirmizi disabled:opacity-40"
                    >
                      Anahtarı iptal et
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <p className="text-[12.5px] leading-relaxed text-gri">
                    Bu acentaya API anahtarı verilmemiş. Anahtar üretince acentanın sistemi müsaitliğinizi çekip
                    rezervasyonu doğrudan oda planınıza yazabilir.
                  </p>
                  {yazabilir && (
                    <button
                      onClick={() => onUret(k)}
                      disabled={islenen === k.id}
                      className="rounded-full bg-marka-600 py-2.5 text-[13px] font-semibold text-white transition hover:bg-marka-700 disabled:opacity-50"
                    >
                      {islenen === k.id ? 'Üretiliyor…' : 'Bağlantı anahtarı üret'}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <OtaAciklamasi />
    </>
  );
}

/** Tasarimdaki "bagli OTA'lar" bolumunun durust karsiligi. */
function OtaAciklamasi() {
  return (
    <section className="flex flex-col gap-3 rounded-[20px] border-[1.5px] border-dashed border-cizgi bg-white p-5">
      <div className="flex items-start gap-3">
        <span className="grid size-9 flex-none place-items-center rounded-[10px] bg-[#fff5dc] text-[#b9840f]">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9.5" />
            <path d="M12 7.5v5m0 3.5v.01" />
          </svg>
        </span>
        <div className="flex flex-col gap-1">
          <h3 className="text-[15px] font-bold text-lacivert">Yurt dışı kanallar (Booking.com, Expedia, Airbnb)</h3>
          <p className="max-w-[80ch] text-[13.5px] leading-relaxed text-gri">
            Bu kanallara <strong className="text-lacivert">doğrudan bağlantı yok</strong> ve burada varmış gibi
            gösterilmiyor. Her biri imzalı ticari sözleşme ve sağlayıcı sertifikasyonu istiyor.
            Ayrıca Booking.com&apos;da <strong className="text-lacivert">Türkiye&apos;den yapılan aramalarda
            Türkiye&apos;deki tesisler görünmüyor</strong> (2017&apos;den beri süren erişim kısıtı); yurt dışındaki
            misafir rezervasyon yapabiliyor. Ağustos 2026&apos;da TBMM&apos;ye sunulan düzenleme bunu
            değiştirebilir. Odalarınızın satışta olduğunu sanıp fazla satmamanız için durumu net söylüyoruz.
          </p>
          <p className="max-w-[80ch] text-[13.5px] leading-relaxed text-gri">
            Yurt içinde satış çoğunlukla <strong className="text-lacivert">Etstur/Odamax, Tatilsepeti, Otelz</strong>
            gibi kanallardan ve bir <strong className="text-lacivert">kanal yöneticisi</strong> üzerinden yürür.
            Kanal yöneticiniz otelcms&apos;in Public API&apos;sine bağlanarak müsaitlik çeker ve rezervasyonu
            buraya yazar; ayrı bir entegrasyon gerekmez.
          </p>
        </div>
      </div>
      <div className="grid gap-2.5 sm:grid-cols-2">
        {(
          [
            [
              'Bugün yapabilecekleriniz',
              'Bir kanal yöneticisi kullanıyorsanız (örn. bir Türk sağlayıcı), ona buradaki Public API anahtarını verin: müsaitlik ve fiyatı çeker, rezervasyonu oda planınıza yazar. Aynı yöntem acentalar için de geçerli.',
            ],
            [
              'Doğrudan entegrasyon isterseniz',
              'İlgili kanalla sözleşme imzalandıktan sonra her biri için ayrı bağlantı yazılması gerekir. Süre ve maliyeti kanala göre değişir; bize yazın, birlikte planlayalım.',
            ],
          ] as const
        ).map(([b, m]) => (
          <div key={b} className="rounded-[14px] bg-zemin p-3.5">
            <span className="block text-[13.5px] font-bold text-lacivert">{b}</span>
            <span className="mt-1 block text-[12.5px] leading-relaxed text-gri">{m}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------- fiyat dagitimi */

function FiyatDagitimi({ veri, acentalar }: { veri: Veri; acentalar: Kanal[] }) {
  const tarih = new Date(`${veri.ornekTarih}T00:00:00`).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    weekday: 'long',
  });

  return (
    <div className="overflow-hidden rounded-[20px] border border-cizgi bg-white [animation:rise_.4s_both] shadow-[0_30px_60px_-40px_rgba(11,21,48,.25)]">
      <div className="flex flex-wrap items-center gap-3 border-b border-cizgi bg-[#fbfcfe] px-6 py-4">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-base font-bold text-lacivert">Fiyat dağıtımı</h3>
          <span className="text-[13px] text-gri">
            {tarih} tarifesi · acenta komisyonu düşülünce elinize ne geçtiğini gösterir.
          </span>
        </div>
      </div>

      {acentalar.length === 0 ? (
        <p className="px-6 py-12 text-center text-sm text-gri">
          Tanımlı acenta yok. Acenta eklendiğinde sözleşme fiyatı ve net kazancınız burada karşılaştırılır.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-satir text-left text-[11px] font-bold uppercase tracking-[.06em] text-gri">
                <th className="px-6 py-3 font-bold">Oda tipi</th>
                <th className="px-4 py-3 font-bold text-marka-600">Baz fiyat</th>
                {acentalar.map((a) => (
                  <th key={a.id} className="px-4 py-3 font-bold">
                    <span className="flex items-center gap-2">
                      <span
                        className="grid size-5 place-items-center rounded-md text-[10px] font-bold text-white"
                        style={{ background: renkSec(a.id) }}
                      >
                        {harf(a.ad)}
                      </span>
                      <span className="truncate normal-case">{a.ad}</span>
                      <span className="font-medium normal-case text-gri">%{a.komisyonYuzde}</span>
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {veri.fiyatDagitimi.map((r) => (
                <tr key={r.roomTypeId} className="border-b border-satir">
                  <td className="px-6 py-3.5">
                    <span className="block font-semibold text-lacivert">{r.ad}</span>
                    <span className="text-xs text-gri">{r.odaSayisi} oda</span>
                  </td>
                  <td className="px-4 py-3.5">
                    {r.bazKurus > 0 ? (
                      <span className="text-[15px] font-bold tabular-nums text-marka-600">{tl(r.bazKurus)}</span>
                    ) : (
                      <span className="text-[13px] text-gri">tarife yok</span>
                    )}
                  </td>
                  {r.satirlar.map((s) => (
                    <td key={s.kanalId} className="px-4 py-3.5">
                      {s.satisKurus > 0 ? (
                        <>
                          <span className="block text-[15px] font-bold tabular-nums text-lacivert">
                            {tl(s.satisKurus)}
                          </span>
                          <span className="block text-[11px] text-gri">
                            size kalan {tl(s.netKurus)}
                            {!s.ozelPlanVarMi && ' · baz tarife'}
                          </span>
                        </>
                      ) : (
                        <span className="text-[13px] text-gri">—</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="border-t border-satir bg-[#fbfcfe] px-6 py-3.5 text-[13px] leading-relaxed text-gri">
        Fiyatlar tarife takviminizden okunur. Acentaya özel sözleşme fiyatı tanımlıysa o kullanılır; yoksa baz tarife
        üzerinden komisyon düşülür. Değiştirmek için tarife takvimini güncelleyin — kanallar müsaitliği bu ekrandan
        değil, API'den canlı çeker.
      </p>
    </div>
  );
}

/* ---------------------------------------------------------------- gunluk */

function Gunluk({ veri }: { veri: Veri }) {
  const kanalAdi = (kaynak: string, agencyId: string | null) => {
    if (agencyId) return veri.kanallar.find((k) => k.id === agencyId)?.ad ?? 'Acenta';
    return KAYNAK_ADI[kaynak] ?? kaynak;
  };

  return (
    <div className="overflow-hidden rounded-[20px] border border-cizgi bg-white [animation:rise_.4s_both]">
      <div className="flex items-center justify-between border-b border-cizgi bg-[#fbfcfe] px-6 py-4">
        <h3 className="text-base font-bold text-lacivert">Kanaldan gelen rezervasyonlar</h3>
        <span className="flex items-center gap-1.5 text-xs text-gri">
          <span className="size-2 rounded-full bg-yesil [animation:blink_1.4s_infinite]" />
          canlı
        </span>
      </div>

      {veri.gunluk.length === 0 ? (
        <p className="px-6 py-12 text-center text-sm text-gri">
          Henüz kanaldan gelen rezervasyon yok. Web modülünden ya da acenta API'sinden gelen kayıtlar burada listelenir.
        </p>
      ) : (
        veri.gunluk.map((g, i) => (
          <div
            key={g.id}
            className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3.5 border-b border-satir px-6 py-3.5 [animation:rise_.4s_both]"
            style={{ animationDelay: `${i * 25}ms` }}
          >
            <span
              className="grid size-8 place-items-center rounded-lg text-[11px] font-bold text-white"
              style={{ background: g.agencyId ? renkSec(g.agencyId) : '#0b1530' }}
            >
              {harf(kanalAdi(g.kaynak, g.agencyId))}
            </span>
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-semibold text-lacivert">
                {kanalAdi(g.kaynak, g.agencyId)} · {g.kod}
              </span>
              <span className="truncate text-xs text-gri">
                {g.girisTarihi} → {g.cikisTarihi} · {tl(g.toplamKurus)} · {anEtiket(g.createdAt)}
              </span>
            </div>
            <span
              className="whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold"
              style={
                g.durum === 'IPTAL' || g.durum === 'NO_SHOW'
                  ? { background: '#fdeceb', color: '#c0392b' }
                  : { background: '#e6f7ec', color: '#0f7a3d' }
              }
            >
              {g.durum === 'IPTAL' ? 'iptal' : g.durum === 'NO_SHOW' ? 'gelmedi' : 'oda planında'}
            </span>
          </div>
        ))
      )}
    </div>
  );
}

/* ------------------------------------------------- tek seferlik anahtar */

function AnahtarPenceresi({
  bilgi,
  onKapat,
}: {
  bilgi: { ad: string; anahtar: string };
  onKapat: () => void;
}) {
  const [kopyalandi, setKopyalandi] = React.useState(false);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-lacivert/60 p-4 [animation:fadein_.2s_both]" role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-[520px] rounded-[20px] bg-white p-6 shadow-[0_40px_80px_-32px_rgba(11,21,48,.55)] [animation:popin_.3s_cubic-bezier(.2,.8,.2,1)_both]"
      >
        <h2 className="text-[20px] font-bold tracking-[-.01em] text-lacivert">{bilgi.ad} bağlantı anahtarı</h2>
        <p className="mt-1 text-[13.5px] leading-relaxed text-gri">
          Bu anahtar <strong className="text-lacivert">yalnızca şimdi</strong> görünür; sistemde sadece şifreli özeti
          saklanıyor. Acentanın teknik ekibine iletin. Kaybolursa iptal edip yenisini üretin.
        </p>

        <pre className="mt-4 overflow-x-auto rounded-[14px] bg-lacivert p-4 font-mono text-[13px] leading-relaxed text-[#d7e0f7]">
          {bilgi.anahtar}
        </pre>

        <p className="mt-3 rounded-xl bg-zemin px-3.5 py-3 text-[12.5px] leading-relaxed text-gri">
          Kullanımı: isteklerde <code className="rounded bg-white px-1.5 py-0.5">X-API-Key</code> başlığı ile gönderilir.
          Uçlar: <code className="rounded bg-white px-1.5 py-0.5">/api/v1/musaitlik</code> ve{' '}
          <code className="rounded bg-white px-1.5 py-0.5">/api/v1/rezervasyonlar</code>.
        </p>

        <div className="mt-4 flex gap-3">
          <button
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(bilgi.anahtar);
                setKopyalandi(true);
                setTimeout(() => setKopyalandi(false), 1600);
              } catch {
                setKopyalandi(false);
              }
            }}
            className="flex-1 rounded-full border-[1.5px] border-cizgi bg-white py-3 text-[15px] font-semibold text-lacivert transition hover:border-lacivert"
          >
            {kopyalandi ? 'Kopyalandı' : 'Anahtarı kopyala'}
          </button>
          <button
            type="button"
            onClick={onKapat}
            className="flex-1 rounded-full bg-marka-600 py-3 text-[15px] font-semibold text-white transition hover:bg-marka-700"
          >
            İlettim, kapat
          </button>
        </div>
      </div>
    </div>
  );
}
